// src/api/baseApi.ts
// RTK Query base API with automatic token refresh on 401
//
// CENTRAL MODE RULES:
// 1. All RTK queries are BLOCKED when bootStatus !== 'ready' (prevents stale-token requests)
// 2. Token refresh goes directly to Central's OIDC token endpoint (NOT the removed /api/central/auth/refresh proxy)
// 3. 409 WORKSPACE_CLOSED triggers sticky overlay via bootContextSlice

import {
  createApi,
  fetchBaseQuery,
  BaseQueryFn,
  FetchArgs,
  FetchBaseQueryError,
} from '@reduxjs/toolkit/query/react';
import type { RootState, AppDispatch } from '../store/store';
import { isCanonicalRpStrategy, indicatesLostSession } from '../auth/authStrategy';
import { beginCanonicalRecovery } from '../auth/canonicalRecovery';
import {
  updateCentralAccessToken,
  centralLogout,
  selectCentralRefreshToken,
} from '../store/slices/centralAuthSlice';
import { setBootWorkspaceClosed } from '../store/slices/bootContextSlice';
import { logout as legacyLogout } from '../store/slices/authSlice';
import { AUTH_MODE, CENTRAL_URL, APP_ID, TESTING_MODE_ROLE_HEADER } from '../config/centralAuth';
import { Mutex } from 'async-mutex';

// Mutex to prevent multiple simultaneous refresh attempts
const refreshMutex = new Mutex();

// Track if we just loaded from localStorage (prevent premature logout on page load)
let hasAttemptedInitialLoad = false;
if (typeof window !== 'undefined') {
  // Mark as loaded after a short delay to allow initial API calls to complete
  setTimeout(() => {
    hasAttemptedInitialLoad = true;
  }, 2000);
}

/**
 * Base query with automatic token injection.
 * Reads the CURRENT token from Redux state at request time (no stale closures).
 */
const baseQuery = fetchBaseQuery({
  baseUrl: '/api',
  credentials: 'include',
  prepareHeaders: (headers, { getState }) => {
    const state = getState() as RootState;

    // Prefer Central auth token if available (for hybrid/central mode)
    // Fall back to legacy auth token for backward compatibility
    const centralToken = state.centralAuth?.accessToken;
    const legacyToken = state.auth.token;
    const token = centralToken || legacyToken;

    if (token) {
      headers.set('Authorization', `Bearer ${token}`);
    }

    // Unified AI surface header — tells the server this is the web client
    headers.set('X-LevelUp-Surface', 'web');

    // Testing mode: forward the override role so the backend can use it
    // for role-sensitive responses (e.g., menu template selection).
    // Central does not re-issue JWT tokens for testing mode overrides,
    // so the token's role claim still contains the real role.
    const meta = state.bootContext?.meta;
    if (meta?.testingMode?.active && meta.testingMode.override?.role) {
      headers.set(TESTING_MODE_ROLE_HEADER, meta.testingMode.override.role);
    }

    return headers;
  },
});

// ============================================================
// Central-direct token refresh (standard OIDC, NOT session/proxy)
// ============================================================

/** Result of a Central token refresh. */
interface RefreshResult {
  accessToken: string;
  /** New refresh token if the OIDC server rotated it. null = keep existing. */
  refreshToken: string | null;
  expiresIn: number;
}

/**
 * Refresh the Central access token directly via Central's OIDC token endpoint.
 *
 * Uses standard OIDC grant_type=refresh_token at {CENTRAL_URL}/api/oidc/token.
 * This is a stateless bearer-token exchange — no session cookies required.
 *
 * Does NOT call the removed /api/central/auth/refresh backend proxy.
 */
async function refreshCentralToken(
  refreshToken: string
): Promise<RefreshResult | null> {
  // In non-central modes, this function should not be called.
  // Fail safe if CENTRAL_URL is not configured.
  if (!CENTRAL_URL) {
    return null;
  }

  try {
    const response = await fetch(`${CENTRAL_URL}/api/oidc/token`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
      body: new URLSearchParams({
        grant_type: 'refresh_token',
        refresh_token: refreshToken,
        client_id: APP_ID,
      }),
    });

    if (!response.ok) {
      return null;
    }

    const data = await response.json();

    // Standard OIDC token response may use snake_case or camelCase
    const accessToken = data.access_token || data.accessToken;
    const expiresIn = data.expires_in ?? data.expiresIn ?? 3600;
    // OIDC servers may rotate refresh tokens — capture new one if present
    const newRefreshToken = data.refresh_token || data.refreshToken || null;

    if (!accessToken) {
      return null;
    }

    return { accessToken, refreshToken: newRefreshToken, expiresIn };
  } catch {
    return null;
  }
}

/**
 * EXPORTED: Safe helper for proactive token refresh.
 * Uses the same mutex as reactive (401-based) refresh to ensure single-flight behavior.
 *
 * This function is safe to call from proactive refresh hooks without risking concurrent refresh attempts.
 * The mutex ensures only one refresh happens at a time, whether triggered proactively or by a 401 response.
 *
 * @param refreshToken - The refresh token to use
 * @returns Refresh result with new access token, optional rotated refresh token, and expiry, or null if refresh failed
 */
export async function proactiveRefreshCentralToken(
  refreshToken: string
): Promise<RefreshResult | null> {
  // Check if a refresh is already in progress
  if (refreshMutex.isLocked()) {
    // Wait for the in-progress refresh to complete, then return null
    // (the in-progress refresh will update the store, so caller doesn't need to do anything)
    await refreshMutex.waitForUnlock();
    return null;
  }

  // Acquire mutex for single-flight refresh
  const release = await refreshMutex.acquire();

  try {
    const result = await refreshCentralToken(refreshToken);
    return result;
  } finally {
    release();
  }
}

// ============================================================
// Boot-status gating + reauth
// ============================================================

/**
 * Check if the app is in central mode (central or hybrid with active Central auth).
 */


/** The request path, for classifying whether a 401 means "session lost". */
function requestUrlOf(a: string | FetchArgs): string {
  return typeof a === 'string' ? a : (a?.url ?? '');
}

/**
 * Does the BROWSER hold a Central access token?
 *
 * Renamed from `isCentralMode`, which is what it was called while it meant
 * this. That name claimed to answer "which auth strategy owns this browser?",
 * and answering that from token presence IS the defect: a canonical RP browser
 * holds no token by design, so the predicate reported `false` for the very
 * architecture it was meant to describe, and every recovery branch gated on it
 * became unreachable.
 *
 * The question itself is still legitimate — it decides whether there is a token
 * to refresh. It is simply not a question about strategy. For that, use
 * `isCanonicalRpStrategy` from `auth/authStrategy`.
 */
function hasBrowserCentralToken(state: RootState): boolean {
  if (AUTH_MODE === 'legacy') return false;
  return Boolean(state.centralAuth?.accessToken);
}

/**
 * Correlation id for ONE LOGICAL USER ACTION.
 *
 * The unit is the action, not the HTTP attempt. `baseQueryWithReauth` runs once
 * per logical request and may put TWO requests on the wire -- the original and
 * the replay after a 401 token refresh -- so the id is minted here, once, and
 * both attempts carry it. Two independent actions get two different ids.
 *
 * That is what makes a refresh-induced duplicate legible: if one gesture
 * produces two provisioning sessions, both Payments log lines carry the SAME
 * request_id and the pair is visibly one action. Minting per attempt instead
 * would make them look like two independent intents, which is the ambiguity
 * this whole mechanism exists to remove.
 *
 * DIAGNOSTIC METADATA ONLY. It is not authentication, authorization, tenancy,
 * idempotency, deduplication, or any payment, instrument or provider identity.
 * The server treats it as untrusted caller input, because it is.
 *
 * Shape matches CRM's `requestId` middleware exactly -- `^[a-zA-Z0-9\-_]+$`,
 * 128 chars max -- so the server preserves it rather than discarding it and
 * generating its own.
 */
const REQUEST_ID_HEADER = 'x-request-id';

function newCorrelationId(): string {
  const c = globalThis.crypto;
  if (c && typeof c.randomUUID === 'function') return c.randomUUID();
  // Older/insecure contexts have no randomUUID. Still hex, still in the
  // server's allowlist, still collision-resistant enough for a log join.
  if (c && typeof c.getRandomValues === 'function') {
    const bytes = c.getRandomValues(new Uint8Array(16));
    return Array.from(bytes, (b) => b.toString(16).padStart(2, '0')).join('');
  }
  return `r${Date.now().toString(36)}${Math.random().toString(36).slice(2, 12)}`;
}

/**
 * Attach the correlation id to the outgoing request.
 *
 * Returns FetchArgs either way: a bare-string arg becomes `{ url }` so the
 * header has somewhere to live. Existing headers are preserved -- `set` only
 * replaces a correlation id, and only this function writes one.
 */
function withCorrelationId(args: string | FetchArgs, correlationId: string): FetchArgs {
  const base: FetchArgs = typeof args === 'string' ? { url: args } : args;
  const headers = new Headers((base.headers as HeadersInit | undefined) ?? undefined);
  headers.set(REQUEST_ID_HEADER, correlationId);
  return { ...base, headers };
}

/**
 * Base query with:
 * 1. Boot-status gating (blocks requests when boot is not ready in central mode)
 * 2. Automatic 401 handling with Central-direct token refresh
 * 3. 409 WORKSPACE_CLOSED sticky handling
 */
const baseQueryWithReauth: BaseQueryFn<
  string | FetchArgs,
  unknown,
  FetchBaseQueryError
> = async (args, api, extraOptions) => {
  const state = api.getState() as RootState;

  // ── GATE: Block all RTK queries while boot is not ready in central mode ──
  // This prevents stale-token requests during workspace switches and initial boot.
  // BootGate sets bootStatus='loading' BEFORE token swap, so components that
  // somehow remain mounted cannot fire queries with the old token.
  if (hasBrowserCentralToken(state)) {
    const bootStatus = state.bootContext?.bootStatus;
    if (bootStatus !== 'ready') {
      // Return a controlled error — RTK Query will NOT retry this automatically.
      // Once boot completes and components re-render, queries will naturally re-fire.
      return {
        error: {
          status: 'CUSTOM_ERROR',
          error: 'Boot not ready',
          data: { code: 'BOOT_NOT_READY', bootStatus },
        } as FetchBaseQueryError,
      };
    }
  }

  // Wait if another refresh is in progress
  await refreshMutex.waitForUnlock();

  // Minted ONCE per logical action, before the first attempt, and reused by
  // every replay below. See `newCorrelationId`.
  const correlatedArgs = withCorrelationId(args, newCorrelationId());

  let result = await baseQuery(correlatedArgs, api, extraOptions);

  // Handle 401 Unauthorized
  if (result.error && result.error.status === 401) {
    const currentState = api.getState() as RootState;
    const refreshToken = selectCentralRefreshToken(currentState);
    const isCentral = hasBrowserCentralToken(currentState);

    // CANONICAL RP: the server owns the session, so re-authentication is a
    // server round trip — there is nothing in this browser to refresh.
    //
    // Gated on the STRATEGY (deployment configuration), never on token
    // presence. The previous attempt gated on a token-derived predicate that is
    // false by definition in canonical mode, so it could never run: an expired
    // RP session produced an endless 401 poll on /auth/access-version and
    // /api/contacts with no redirect and no recovery.
    //
    // Latched: the first request that proves the session is gone starts exactly
    // ONE navigation and every later 401 is absorbed, so a poll storm cannot
    // become a navigation storm.
    //
    // Auth-plumbing URLs are excluded — they answer 401 as normal operation,
    // and treating that as session loss is how a recovery loop forms.
    if (
      isCanonicalRpStrategy() &&
      !refreshToken &&
      hasAttemptedInitialLoad &&
      indicatesLostSession(requestUrlOf(args))
    ) {
      beginCanonicalRecovery();
      return result;
    }

    // DEV ONLY: Log 401 handling
    if (import.meta && import.meta.env && import.meta.env.DEV) {
      console.log('[baseApi] 401 response, attempting token refresh:', {
        isCentral,
        hasRefreshToken: !!refreshToken,
        hasAttemptedInitialLoad,
        bootStatus: currentState.bootContext?.bootStatus,
        endpoint: typeof args === 'string' ? args : args.url,
      });
    }

    // CRITICAL: Do NOT attempt token refresh during workspace switch.
    // A refresh during switch could return a token for the OLD workspace.
    // The request that triggered this 401 was likely sent just before the boot gate kicked in.
    if (isCentral && currentState.bootContext?.bootStatus !== 'ready') {
      return result;
    }

    if (isCentral && refreshToken) {
      // Try to refresh token via Central OIDC (with mutex to prevent concurrent refreshes)
      if (!refreshMutex.isLocked()) {
        const release = await refreshMutex.acquire();

        try {
          // Re-read state after acquiring mutex — boot status or token may have changed
          // (e.g., workspace switch started while we were waiting for the mutex)
          const freshState = api.getState() as RootState;
          if (freshState.bootContext?.bootStatus !== 'ready') {
            return result;
          }

          const freshRefreshToken = selectCentralRefreshToken(freshState);

          if (freshRefreshToken) {
            const refreshResult = await refreshCentralToken(freshRefreshToken);

            if (refreshResult) {
              // Update tokens in store (including rotated refresh token if present)
              (api.dispatch as AppDispatch)(
                updateCentralAccessToken({
                  accessToken: refreshResult.accessToken,
                  expiresIn: refreshResult.expiresIn,
                  refreshToken: refreshResult.refreshToken,
                })
              );

              // Retry the original request with new token. Same
              // `correlatedArgs`, so the replay keeps the action's id.
              result = await baseQuery(correlatedArgs, api, extraOptions);
            } else {
              // Re-check boot status before logout — switch may have started during refresh attempt
              const postRefreshState = api.getState() as RootState;
              if (postRefreshState.bootContext?.bootStatus !== 'ready') {
                return result;
              }
              // Refresh failed — fail closed: logout
              if (hasAttemptedInitialLoad) {
                console.warn('[baseApi] Central token refresh failed, logging out');
                (api.dispatch as AppDispatch)(centralLogout());
              } else {
                console.warn('[baseApi] Token refresh failed during initial load, skipping auto-logout');
              }
            }
          }
        } finally {
          release();
        }
      } else {
        // Another refresh is in progress, wait for it and retry
        await refreshMutex.waitForUnlock();
        // Re-check boot status after waiting — workspace switch may have started
        const postWaitState = api.getState() as RootState;
        if (postWaitState.bootContext?.bootStatus !== 'ready') {
          return result;
        }
        // Same `correlatedArgs` here too: waiting on another tab's refresh
        // does not make this a different user action.
        result = await baseQuery(correlatedArgs, api, extraOptions);
      }
    } else if (!isCentral && currentState.auth.token) {
      // Legacy auth - just logout on 401
      (api.dispatch as AppDispatch)(legacyLogout());
    } else if (isCentral && !refreshToken) {
      // Central auth but no refresh token.
      // This is EXPECTED after a workspace switch — Central's select-workspace
      // does not return a refresh_token. The access token is valid but non-renewable.
      // Do NOT logout here — the access token may still work for subsequent requests.
      // If the token is truly expired, the user will need to re-authenticate via Central.
      if (import.meta.env.DEV) {
        console.warn('[baseApi] 401 with no refresh token — cannot refresh, returning error');
      }
    }
  }

  // Handle 409 WORKSPACE_CLOSED from ANY API call (sticky — blocks UI)
  if (result.error && result.error.status === 409) {
    const data = result.error.data as any;
    if (data?.code === 'WORKSPACE_CLOSED') {
      // Immediately enter workspace_closed state — WorkspaceClosedOverlay will block UI
      (api.dispatch as AppDispatch)(
        setBootWorkspaceClosed({ otherWorkspaces: data.otherWorkspaces || [] })
      );
      // Clear all caches to prevent stale data
      (api.dispatch as AppDispatch)(baseApi.util.resetApiState());
    }
  }

  return result;
};

export const baseApi = createApi({
  reducerPath: 'api',
  baseQuery: baseQueryWithReauth,
  tagTypes: [
    'Workspace',
    'ExecutionState',
    'Menu',
    'Conversation',
    'ConversationAction',
    'Credits',
    'Contact',
    'Task',
    'TaskChecklistItem',
    // Work Accountability (PR-E)
    'AccountabilityPolicy',
    'AccountabilityPersonRule',
    'AccountabilityEffective',
    'TaskTimeWA',
    'TaskCompletion',
    'TaskTimer',
    'Reconciliation',
    'TaskTemplate',
    'Pipeline',
    'Opportunity',
    'Event',
    'Circle',
    'ProviderRole',
    'ProviderAssignment',
    // Compliance domain
    'ComplianceCategory',
    'ComplianceRequirement',
    'ComplianceAssignment',
    'ComplianceDocument',
    'ComplianceApproval',
    // LevelUp Payments integration (backend PR #252).
    'PaymentsConfig',
    'PaymentInstrument',
    'ComplianceReview',
    'ComplianceEligibility',
    'ComplianceCoverage',
    'Referral',
    'Offering',
    'Payment',
    'Team',
    'TimeEntry',
    'File',
    'Form',
    'Project',
    'BusinessProfile',
    'Portfolio',
    'PortfolioCategory',
    'Thread',
    'Message',
    'MessageFolder',
    'SavedMessage',
    'Channel',
    'ChannelSubscription',
    'ChannelExecution',
    'ChannelContent',
    'RecruitmentOpportunity',
    'RecruitmentApplication',
    'TalentProfile',
    'MarketplaceSearch',
    'MarketplaceFeatured',
    'MarketplaceTalent',
    'MarketplaceOpportunities',
    'MarketplaceOfferings',
    'MarketplaceChannels',
    'Integration',
    'TwilioPhoneNumber',
    'CallSession',
    'Voicemail',
    // ADDITIVE (inbound calling) — Outbound-proxied received calls + voicemails.
    'InboundCall',
    // PitchLess materials, surfaced through the CRM BFF. CRM stores no row per
    // material, so these tags key a CACHE of PitchLess's answers, nothing more:
    // any mutation invalidates and re-reads rather than patching local state,
    // because PitchLess is the only thing that knows the truth.
    'Material',
    'MaterialReview',
    'MaterialVersion',
    'DefaultOutboundNumber',
    'VoiceConfig',
    'Billing',
    'BillingPurchases',
    'WeOpenSession',
    'AuditLogs',
    'UserPermissions',
    'GlobalSearch',
    'AIMemory',
    'Automation',
    'Onboarding',
    'Demo',
    'Auth',
    'Notification',
    'Attendance',
    'Activity',
    'VoiceAgent',
    'ThreadParticipant',
    'ThreadPreference',
    'PinnedMessage',
    'MessageReaction',
    'Company',
    'MenuTemplate',
    'Goal',
    // Phase 4 PR4 — WeOpen convergence. Distinct from 'Conversation', which is
    // CRM's own thread domain: these tag WeOpen-owned state that CRM only reads.
    'WeOpenConversation',
    'WeOpenMessages',
  ],
  // PR-27.4 — auto-refresh queries when the browser tab regains focus
  // or the connection comes back. Belt-and-suspenders for the realtime
  // listener in useRealtimeCacheUpdates: if a sibling app (Outbound,
  // Scheduler, etc.) writes to CRM while this tab was backgrounded —
  // or while the websocket was reconnecting — the data refreshes the
  // instant the user clicks back to the CRM tab. RTK Query defaults
  // both to OFF; this is the only place to opt in globally.
  refetchOnFocus: true,
  refetchOnReconnect: true,
  endpoints: () => ({}),
});
